import React, {useCallback, useMemo} from 'react'
import { animated, useSprings } from 'react-spring'

const pointSet = [
    'M0,0 L 0,100 L 100,100Z',
    'M0,100 L 100,100 L 100,0Z',
    'M100,100 L 100,0 L 0,0Z',
    'M100,0 L 0,0 L 0,100Z'
]

const Kaleidoscope = React.forwardRef(({
    colors,
    dimensions,
    duration,
    isReversed,
    isStaggered,
    isUpdating,
    squareSize,
    stagger,
    updateValue,
    values
}, ref) => {
    const quadrantSize = dimensions * squareSize
    const frameSize = quadrantSize * 2
    const scale = squareSize / 100
    const animationConfig = useMemo(() => ({
        precision: 0.1,
        duration
    }), [duration])
    const cellMeta = useMemo(() => {
        const denominator = Math.max(1, (dimensions * 2) - 2)

        return Array.from({length: dimensions * dimensions}, (_, index) => {
            const x = index % dimensions
            const y = Math.floor(index / dimensions)
            const distanceFromOuterEdge = Math.max(x, y)

            return {
                x,
                y,
                colorOffset: (x + y) / denominator,
                distanceFromOuterEdge,
                distanceFromCenter: (dimensions - 1) - distanceFromOuterEdge
            }
        })
    }, [dimensions])
    const cellTransforms = useMemo(() => {
        return cellMeta.map(({x, y}) => `translate(${x * squareSize} ${y * squareSize}) scale(${scale})`)
    }, [cellMeta, scale, squareSize])
    const getSpringProps = useCallback((i, nextValues) => {
        const cell = cellMeta[i]
        const waveOffset = isReversed
            ? cell.distanceFromOuterEdge
            : cell.distanceFromCenter

        return {
            shape: pointSet[nextValues[i]],
            fill: colors(cell.colorOffset),
            config: animationConfig,
            delay: isUpdating && isStaggered ? waveOffset * stagger : 0
        }
    }, [animationConfig, cellMeta, colors, isReversed, isStaggered, isUpdating, stagger])

    const [springs] = useSprings(
        values.length,
        i => getSpringProps(i, values),
        [getSpringProps, values]
    )

    const clickHandlers = useMemo(() => {
        return cellMeta.map((_, index) => event => {
            event.stopPropagation()
            updateValue(index)
        })
    }, [cellMeta, updateValue])

    const renderQuadrant = useCallback((angle, offsetX, offsetY) => (
        <g transform={`translate(${offsetX} ${offsetY})`}>
            <g transform={`rotate(${angle} ${quadrantSize / 2} ${quadrantSize / 2})`}>
                {springs.map(({fill, shape}, i) => {
                    return (
                        <g key={`${angle}-${i}`} transform={cellTransforms[i]}>
                            <rect
                                x="0"
                                y="0"
                                width="100"
                                height="100"
                                fill="transparent"
                                pointerEvents="all"
                                onClick={clickHandlers[i]}
                            />
                            <animated.path d={shape} style={{fill}} pointerEvents="none"/>
                        </g>
                    )
                })}
            </g>
        </g>
    ), [cellTransforms, clickHandlers, quadrantSize, springs])

    return (
        <svg
            ref={ref}
            width="100%"
            height="100%"
            viewBox={`0 0 ${frameSize} ${frameSize}`}
            role="presentation"
        >
            {renderQuadrant(0, 0, 0)}
            {renderQuadrant(90, quadrantSize, 0)}
            {renderQuadrant(180, quadrantSize, quadrantSize)}
            {renderQuadrant(270, 0, quadrantSize)}
        </svg>
    )
})

Kaleidoscope.displayName = 'Kaleidoscope'

export default React.memo(Kaleidoscope)
